import React from 'react';

import type { TextProps } from '@zillow/constellation';
import {
    Box,
    Flex,
    IconButton,
    IconChevronRightOutline,
    IconCloseCircle,
    Text,
} from '@zillow/constellation';
import { useYieldCallback } from '@zillow/yield-callback';

import { JumpBackInCardSkeleton } from './JumpBackInCardSkeleton';
import { JumpBackInCardWrapper } from './JumpBackInCardWrapper';

type TruncatedTextProps = TextProps & {
    lines: number;
};
const TruncatedText = ({
    children,
    lines,
    ...textProps
}: React.PropsWithChildren<TruncatedTextProps>) => (
    <Box
        as={Text}
        sx={{
            'display': '-webkit-box',
            '-webkit-line-clamp': `${lines}`,
            '-webkit-box-orient': 'vertical',
            'overflow': 'hidden',
        }}
        {...textProps}
    >
        {children}
    </Box>
);

interface BaseProps {
    loading?: boolean;
    onDismiss?: () => void;
    gated?: boolean;
    leftContent?: React.ReactNode;
}
interface LoadingProps extends BaseProps {
    loading: true;
}
interface LoadedProps extends BaseProps {
    loading?: false;
    body: string;
    href: string;
    title: string;
}
type JumpBackInCardProps = LoadingProps | LoadedProps;

export const JumpBackInCard: React.FC<JumpBackInCardProps> = (props) => {
    const { loading, onDismiss } = props;
    const yieldedDismiss = useYieldCallback(
        onDismiss ??
            (() => {
                // noop
            })
    );

    if (loading) {
        return (
            <JumpBackInCardWrapper>
                <JumpBackInCardSkeleton />
            </JumpBackInCardWrapper>
        );
    }

    const { title, body, href, leftContent, gated } = props;
    return (
        <JumpBackInCardWrapper href={href} gated={gated}>
            {/*  
                    'blur' filter breaks the fixed positioning on the icon
                    let's just hide it when card is gated as it's not meant 
                    to be clickable anyways

                    See: https://stackoverflow.com/questions/52937708/why-does-applying-a-css-filter-on-the-parent-break-the-child-positioning
                */}
            {onDismiss && !gated && (
                <Box
                    as={IconButton}
                    size="md"
                    title="Dismiss"
                    buttonType="secondary"
                    appearance="circle"
                    bare
                    icon={<IconCloseCircle fontColor="gray400" />}
                    sx={{
                        background: 'yellow',
                        // @ts-expect-error !important is not considered as valid
                        position: 'absolute !important',
                        top: 'calc($spacing.lg / -2)',
                        right: 'calc($spacing.lg / -2)',
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        yieldedDismiss();
                    }}
                />
            )}
            {leftContent}
            <Flex display="flex" flexDirection="column" alignSelf="baseline">
                <TruncatedText lines={1} fontType="h6">
                    {title}
                </TruncatedText>
                <TruncatedText lines={2} fontType="bodySmall">
                    {body}
                </TruncatedText>
            </Flex>
            <IconChevronRightOutline size="xs" fontColor="gray600" />
        </JumpBackInCardWrapper>
    );
};
